/* global google */

import {
    Marker,
    MarkerClusterer,
    SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import LocationProps from "../../types/Location";
import MapSearch from "./MapSearch";
import MapStore from "./MapStore";
import MapUtils from "./MapUtils";
import { mapStyle } from "./style";
import MapStoreProps from "../../types/MapStore";

class MapPage {
    mapContainer: HTMLElement | null;
    map: google.maps.Map | undefined;
    markerCluster: MarkerClusterer | undefined;
    utils: MapUtils | undefined;
    locations: LocationProps[] | undefined;
    store: MapStore | undefined;
    markers: Marker[] | undefined;
    siteTheme: string | undefined;
    data: DOMStringMap | undefined;
    search: MapSearch | undefined;
    config: {
        ZOOM_LEVEL_SELECTED: number;
        SCREEN_MOBILE_MAX: number;
        X_OFFSET_DESKTOP: number;
        STORE_DETAILS_WINDOW_HEIGHT: number;
        UNSELECTED_MARKER_URL: string;
        SELECTED_MARKER_URL: string;
        CLUSTER_MARKER_URL: string;
        MARKER_WIDTH: number;
        MARKER_HEIGHT: number;
    };
    constructor() {
        this.mapContainer = document.getElementById("map");
        this.siteTheme = this.mapContainer?.dataset.siteTheme || "";
        this.config = {
            ZOOM_LEVEL_SELECTED: 16,
            SCREEN_MOBILE_MAX: 575,
            X_OFFSET_DESKTOP: -200,
            STORE_DETAILS_WINDOW_HEIGHT: 200,
            UNSELECTED_MARKER_URL:
                "/static/images/" + this.siteTheme + "/map-pin-unselected.svg",
            SELECTED_MARKER_URL:
                "/static/images/" + this.siteTheme + "/map-pin-selected.svg",
            CLUSTER_MARKER_URL:
                "/static/images/" + this.siteTheme + "/map-pin-cluster.svg",
            MARKER_WIDTH: 35,
            MARKER_HEIGHT: 54,
        };

        if (
            typeof this.mapContainer !== "undefined" &&
            this.mapContainer !== null
        ) {
            this.map = undefined;
            this.markerCluster = undefined;
            this.utils = new MapUtils();
            this.locations = [];
            this.store = new MapStore();
            this.markers = [];
            this.data = this.mapContainer.dataset;
            this.search = new MapSearch(
                this.map,
                this.locations,
                this.selectLocation,
            );

            this.addListeners();
            window.initMap = this.initMap();
        }
    }

    async initMap() {
        const { Map } = (await google.maps.importLibrary(
            "maps",
        )) as google.maps.MapsLibrary;

        // Add the locations
        this.locations = JSON.parse(
            document.getElementById("locations")?.textContent || "",
        );

        this.locations?.sort(function (a, b) {
            const textA = a.Name?.toUpperCase();
            const textB = b.Name?.toUpperCase();
            return textA < textB ? -1 : textA > textB ? 1 : 0;
        });

        // Update the search data
        this.search?.setLocations(this.locations);

        // Set the center
        const mapCenter =
            this.locations && this.locations[0] && this.utils
                ? this.utils.getCoordinatesOfLocation(this.locations[0])
                : { lat: 0, lng: 0 };

        // Init the map
        if (this.mapContainer)
            this.map = new Map(this.mapContainer, {
                center: mapCenter,
                zoom: this.config.ZOOM_LEVEL_SELECTED,
                disableDefaultUI: true,
                styles: mapStyle,
            });

        // Update the search map
        this.search?.setMap(this.map);

        // Add the markers and fit the bounds
        const bounds = new google.maps.LatLngBounds();
        this.locations?.forEach((location) => {
            const coords = this.utils?.getCoordinatesOfLocation(location);
            if (this.utils?.isValidCoords(coords)) {
                const marker = new google.maps.Marker({
                    position: coords,
                    optimized: false,
                    icon: this.markerImageUnselected(),
                    map: this.map,
                });
                marker.addListener("click", () => {
                    this.selectLocation(location);
                    if (this.markers) {
                        this.markers.forEach((m) => {
                            if (m !== marker) {
                                (m as google.maps.Marker).setIcon(
                                    this.markerImageUnselected(),
                                );
                            } else {
                                marker.setIcon(this.markerImageSelected());
                            }
                        });
                    }
                });
                if (coords) {
                    bounds.extend(
                        new google.maps.LatLng(coords.lat, coords.lng),
                    );
                }
                location.marker = marker;
                if (this.markers) this.markers.push(marker);
            }
        });
        this.map?.fitBounds(bounds);

        // Add the map clustering
        this.markerCluster = new MarkerClusterer({
            map: this.map,
            markers: this.markers,
            algorithm: new SuperClusterAlgorithm({ radius: 80 }),
            renderer: {
                render: ({ count, position }) =>
                    new google.maps.Marker({
                        label: {
                            text: String(count),
                            className: "map-cluster-label",
                            color: "none",
                            fontSize: "",
                            fontFamily: "",
                        },
                        icon: {
                            url: this.config.CLUSTER_MARKER_URL,
                        },
                        position,
                        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                    }),
            },
        });

        if (this.map)
            google.maps.event.addListenerOnce(this.map, "idle", () => {
                if (this.data?.preselectedstoreid) {
                    this.selectLocationFromStoreId(
                        this.data.preselectedstoreid.toString(),
                    );
                } else {
                    this.selectNearestStore();
                }
            });
    }

    selectLocationFromStoreId(storeId: string) {
        const store = this.getLocationFromStoreId(storeId);
        if (!store) return;

        this.selectLocationAndSetMarker(store);
    }

    addListeners() {
        document.querySelector("#position")?.addEventListener("click", () => {
            this.selectNearestStore();
        });

        window.addEventListener("selectLocationFromStoreId", (e) => {
            this.selectLocationFromStoreId((e as CustomEvent).detail);
        });
    }

    async selectNearestStore() {
        const position = await this.getCurrentPosition();
        this.selectLocationFromPosition(position as GeolocationPosition);
    }

    getCurrentPosition() {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                (position) => resolve(position),
                (error) => reject(error),
            );
        });
    }

    selectLocationFromPosition(position: GeolocationPosition) {
        const currentPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
        };
        let nearestDistance = Number.MAX_VALUE;
        let nearestLocation = {} as LocationProps;

        if (this.locations && this.locations.length > 0) {
            this.locations.forEach((location) => {
                if (this.utils) {
                    const distance = this.utils.euclidean(
                        this.utils.getCoordinatesOfLocation(location),
                        currentPos,
                    );
                    if (nearestDistance > distance) {
                        nearestDistance = distance;
                        nearestLocation = location;
                    }
                }
            });

            this.selectLocationAndSetMarker(nearestLocation, 1);
        }
    }

    selectLocation(location: LocationProps, isNearest = 0) {
        if (this.utils && this.map) {
            const center = new google.maps.LatLng(
                this.utils.getCoordinatesOfLocation(location),
            );

            const zoom = this.map.getZoom();

            if (zoom && zoom < this.config.ZOOM_LEVEL_SELECTED) {
                this.map.setZoom(this.config.ZOOM_LEVEL_SELECTED);
            }

            this.utils
                .getLocationData(location.StoreId)
                .then((store: MapStoreProps) => {
                    window.history.pushState(null, "", store.URL);

                    if (isNearest) {
                        $("#welcome-label").text(
                            this.data?.nearestStoreWelcomeLabel || "",
                        );
                    } else {
                        $("#welcome-label").text(
                            this.data?.standardStoreWelcomeLabel || "",
                        );
                    }

                    if (this.store) {
                        this.store.setStoreDetails(store);
                        this.store.setOpeningDateInfo(
                            store,
                            this.data?.openingInformationPreText || "",
                        );
                        this.store.setClosingDateInfo(
                            store,
                            this.data?.closingInformationPreText || "",
                        );
                        this.store.setOpeningHours(store);
                        this.store.setUnmannedOpeningHours(store);
                        this.store.setSpecialOpeningHours(store);
                        this.store.handleOpeningHoursBorderLine(store);
                        this.store.setContactInfo(store);
                        this.store.setSocialInfo(store);
                        this.store.setStoreServices(store);
                    }
                });

            if (window.innerWidth > this.config.SCREEN_MOBILE_MAX) {
                this.panMap(
                    center,
                    this.config.X_OFFSET_DESKTOP,
                    this.config.STORE_DETAILS_WINDOW_HEIGHT / 8,
                );
            } else {
                this.panMap(
                    center,
                    0,
                    this.config.STORE_DETAILS_WINDOW_HEIGHT / 2,
                );
            }
        }
    }

    panMap(latlng: google.maps.LatLng, offsetx: number, offsety: number) {
        if (this.map && this.map.getZoom()) {
            const point1 =
                this.map.getProjection()?.fromLatLngToPoint(latlng) ||
                new google.maps.Point(0, 0);
            const point2 = new google.maps.Point(
                (typeof offsetx === "number" ? offsetx : 0) /
                    Math.pow(2, this.map.getZoom() || 1) || 0,
                (typeof offsety === "number" ? offsety : 0) /
                    Math.pow(2, this.map.getZoom() || 1) || 0,
            );

            const newPoint = new google.maps.Point(
                point1.x - point2.x,
                point1.y + point2.y,
            );

            const newPosition = this.map
                ?.getProjection()
                ?.fromPointToLatLng(newPoint);

            if (newPosition) this.map.panTo(newPosition);
        }
    }

    selectLocationAndSetMarker(location: LocationProps, isNearest = 0) {
        this.selectLocation(location, isNearest);
        if (this.utils && this.markers) {
            const coords = this.utils.getCoordinatesOfLocation(location);
            this.markers.forEach((m) => {
                const marker = m as google.maps.Marker;
                const markerCoords = {
                    lat:
                        this.utils?.trimCoordinate(
                            marker.getPosition()?.lat(),
                        ) || 0,
                    lng:
                        this.utils?.trimCoordinate(
                            marker.getPosition()?.lng(),
                        ) || 0,
                };
                if (
                    markerCoords.lat === coords.lat &&
                    markerCoords.lng === coords.lng
                ) {
                    marker.setIcon(this.markerImageSelected());
                } else {
                    marker.setIcon(this.markerImageUnselected());
                }
            });
        }
    }

    getLocationFromStoreId(storeId: string) {
        return this.locations?.find(
            (location: LocationProps) => location.StoreId === storeId,
        );
    }

    markerImageUnselected() {
        return this.markerImage(false);
    }

    markerImageSelected() {
        return this.markerImage(true);
    }

    markerImage(selected: boolean) {
        return {
            url: selected
                ? this.config.SELECTED_MARKER_URL
                : this.config.UNSELECTED_MARKER_URL,
            scaledSize: new google.maps.Size(
                this.config.MARKER_WIDTH,
                this.config.MARKER_HEIGHT,
            ),
        };
    }
}

export default MapPage;
